import React from 'react';

import { AlertDialogTitleHeader } from '@/components/molecules/alert-dialog-title-header';
import { CriticalAlertIcon } from '@/components/molecules/critical-alert-icon';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogBody,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogFooterButtons,
} from '@/ux-common/components/alert-dialog/alert-dialog';
import { Button } from '@/ux-common/components/button/button';
import { Typography } from '@/ux-common/components/typography/typography';

interface DiscardChangesDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onConfirm: () => void | Promise<void>;
  /** Dialog title. Defaults to "Discard Unapplied Changes?" */
  title?: string;
  /** Dialog description. Accepts ReactNode for rich content (e.g. lists). */
  description?: React.ReactNode;
  /** Cancel button label. Defaults to "NO, KEEP EDITING" */
  cancelLabel?: string;
  /** Confirm button label. Defaults to "YES, DISCARD CHANGES" */
  confirmLabel?: string;
}

/**
 * DiscardChangesDialog
 *
 * Reusable confirmation dialog for destructive discard actions.
 * Supports custom title, description, and button labels via optional props.
 * Defaults match the original bulk-edit discard behavior.
 * Uses the registry AlertDialog to match design (Alert icon and destructive button)
 */
export const DiscardChangesDialog = ({
  open,
  onOpenChange,
  onConfirm,
  title = 'Discard Unapplied Changes?',
  description = 'You have unsaved changes that will be lost.',
  cancelLabel = 'NO, KEEP EDITING',
  confirmLabel = 'YES, DISCARD CHANGES',
}: DiscardChangesDialogProps): React.ReactNode => {
  const handleConfirm = async () => {
    await onConfirm();
    onOpenChange(false);
  };

  return (
    <AlertDialog open={open} onOpenChange={onOpenChange}>
      <AlertDialogContent data-component="DiscardChangesDialog" className="dialog-md">
        <AlertDialogTitleHeader icon={<CriticalAlertIcon />} title={title} />
        <AlertDialogBody>
          <AlertDialogDescription asChild>
            <Typography as="div" className="text-body-base">
              {description}
            </Typography>
          </AlertDialogDescription>
        </AlertDialogBody>
        <AlertDialogFooter>
          <AlertDialogFooterButtons>
            <AlertDialogCancel asChild>
              <Button type="button" className="btn-tier3">
                {cancelLabel}
              </Button>
            </AlertDialogCancel>
            <AlertDialogAction asChild>
              <Button type="button" className="btn-destructive" onClick={handleConfirm}>
                {confirmLabel}
              </Button>
            </AlertDialogAction>
          </AlertDialogFooterButtons>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
};
